// Vanilla JavaScript entry point
import { createElement } from "react";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";
import { Widget } from "./components/widget";
import "./global.css";
import { initFeedget } from "./lib/api";

export interface FeedgetConfig {
  apiKey: string;
  apiUrl?: string;
  orientation?: "left" | "right";
  metadata?: {
    userId?: string;
    version?: string;
    custom?: Record<string, any>;
  };
}

class FeedgetWidget {
  private root: Root | null = null;
  private container: HTMLDivElement | null = null;
  private config: FeedgetConfig;

  constructor(config: FeedgetConfig) {
    this.config = config;
    initFeedget(config);
  }

  init(): void {
    // Check if already initialized
    if (this.container) {
      console.warn("Feedget Widget already initialized");
      return;
    }

    // Create container
    this.container = document.createElement("div");
    this.container.id = "feedget-widget-root";
    document.body.appendChild(this.container);

    // Create React root and render widget
    this.root = createRoot(this.container);
    this.root.render(
      createElement(Widget, {
        apiKey: this.config.apiKey,
        apiUrl: this.config.apiUrl,
        orientation: this.config.orientation,
        metadata: this.config.metadata,
      })
    );
  }

  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container) {
      document.body.removeChild(this.container);
      this.container = null;
    }
  }

  updateConfig(newConfig: Partial<FeedgetConfig>): void {
    this.config = { ...this.config, ...newConfig };
    initFeedget(this.config);

    if (this.root) {
      this.root.render(
        createElement(Widget, {
          apiKey: this.config.apiKey,
          apiUrl: this.config.apiUrl,
          orientation: this.config.orientation,
          metadata: this.config.metadata,
        })
      );
    }
  }
}

// Global instance
let feedgetInstance: FeedgetWidget | null = null;

// Global API
export const Feedget = {
  init(config: FeedgetConfig): void {
    if (feedgetInstance) {
      console.warn(
        "Feedget already initialized. Use Feedget.updateConfig() to update configuration."
      );
      return;
    }

    feedgetInstance = new FeedgetWidget(config);
    feedgetInstance.init();
  },

  destroy(): void {
    if (feedgetInstance) {
      feedgetInstance.destroy();
      feedgetInstance = null;
    }
  },

  updateConfig(config: Partial<FeedgetConfig>): void {
    if (feedgetInstance) {
      feedgetInstance.updateConfig(config);
    } else {
      console.error("Feedget not initialized. Call Feedget.init() first.");
    }
  },
};

// Make it available globally for vanilla JavaScript
if (typeof window !== "undefined") {
  (window as any).Feedget = Feedget;
}
